import { z } from 'zod'

import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import {
  checkRateLimit,
  recordCall,
} from '@/Atomic/Widgets/AiChatBot/core/engine/pipeline/write/rate-limiter'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import {
  clearCachedFilter,
  getCachedFilter,
  savedFilterStructureSchema,
} from '@/Atomic/Widgets/AiChatBot/core/tools/filter-normalizer'
import { type ScrivitoAccessor } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'
import { DataClassName } from '@/Data/const'

/**
 * Attaches an advanced filter to an existing dynamic segment (a List record),
 * writing it to `list.query.filters`. Distinct from `save_filter`, which creates
 * a standalone Filter record for the FilterBuilder sidebar; this one persists onto
 * a segment's membership query, exactly like the manual SegmentFilterModal.
 *
 * The segment must already exist. To create a NEW segment, use the dedicated
 * create_segment tool — never this one. This tool is only for REFINING an existing
 * dynamic segment (Flow C).
 *
 * The filter written is resolved by preference: build_filter's exact cached output
 * (getCachedFilter, keyed by the segment's base entity) for the build→refine flow;
 * else the filter currently APPLIED on the list page (getAppliedFilterStructure — "save this
 * filter to a segment"); else the `structure` the model relays as an argument. The
 * relayed arg is last because it drifts across the build_filter → confirm → save turn
 * boundary — the cache and the URL are the trustworthy sources.
 */
export const saveFilterToSegmentTool = defineTool({
  name: 'save_filter_to_segment',
  description:
    'Attach an advanced filter to an existing dynamic segment (List record), saving it to the segment membership query. ' +
    'Two flows: (1) after the user confirms a newly-built filter via show_filter_preview when refining a segment; (2) when the user asks to save the filter ALREADY APPLIED on the current list page ("save this filter", "the applied filter", "the filter on this page") — in that case set `useAppliedFilter: true`, omit `structure`, and the tool uses ONLY the filter applied on the page, ignoring any filter built earlier in the chat. The applied filter is what the user sees on the page, never a filter from the conversation. ' +
    'To create a NEW segment, use create_segment instead — never this tool. ' +
    "Never reconstruct `structure` from conversation context; the tool prefers build_filter's cached structure and the applied-page filter over the passed arg. " +
    'This is for segments (Lists) only; use save_filter to create a standalone saved filter instead.',
  concurrencySafe: false,
  cacheable: false,
  inputSchema: () =>
    z.object({
      segmentId: z
        .string()
        .describe(
          'The _id of the dynamic segment (List) to attach the filter to',
        ),
      structure: z
        .preprocess(
          (v) => (typeof v === 'string' ? JSON.parse(v) : v),
          savedFilterStructureSchema,
        )
        .optional()
        .describe(
          'OPTIONAL. The exact SavedFilterStructure from show_filter_preview. Omit it to save the filter already applied on the list page ("save this filter to a segment") — the tool reads that from the page itself.',
        ),
      useAppliedFilter: z
        .boolean()
        .optional()
        .describe(
          'Set TRUE when the user means the filter ALREADY APPLIED on the list page ("save this filter", "the applied filter", "the filter on this page"). The tool then uses ONLY the applied page filter and ignores any filter built earlier in the chat; if nothing is applied it returns an error rather than falling back. Leave unset for the build → confirm → refine flow.',
        ),
    }),

  async run(args) {
    const { segmentId } = args
    const { data, navigation: nav } = getHostRuntime()

    // Fetch the segment; keep the item reference for the update below. data.get wraps
    // the fetch in a single load(), and reading .get() off the loaded item mirrors the
    // manual SegmentFilterModal read. `query` is the List's untyped membership-query
    // passthrough, written the same way by create_segment.
    const item = (await data.get(
      DataClassName.LIST,
      segmentId,
    )) as ScrivitoAccessor | null
    if (!item) {
      return { error: `Segment with id ${segmentId} not found.` }
    }
    const type = item.get('type')
    const entityType = item.get('entityType')
    const query = item.get('query') as Record<string, unknown> | null

    // Guard 1: advanced filters only apply to dynamic segments. A static segment's
    // membership is a fixed set, mirroring the hidden filter button in the UI —
    // name the real workaround rather than dead-ending.
    if (type !== 'dynamic') {
      return {
        error:
          'This segment is static, so an advanced filter cannot be applied. Static segments take members manually or via CSV import on the segment page.',
      }
    }

    // Resolve the filter to save. When the user means the filter APPLIED ON THE PAGE
    // (useAppliedFilter), that is authoritative — the applied filter is what the user
    // sees, never a filter built earlier in the chat (CRM-1450); if nothing is applied
    // we error rather than silently reusing a stale build-cache. Otherwise (the
    // build→refine flow) prefer build_filter's cached structure for this segment's base
    // entity, then the applied page filter, then the model-relayed arg. The arg is last
    // because it drifts across the build_filter → confirm → save turn boundary — cache +
    // applied-URL are the trustworthy sources.
    const cacheKey = typeof entityType === 'string' ? entityType : undefined
    const appliedFilter = await data.getAppliedFilterStructure()
    const structure = args.useAppliedFilter
      ? appliedFilter
      : ((cacheKey ? getCachedFilter(cacheKey) : undefined) ??
        appliedFilter ??
        args.structure)
    if (!structure) {
      return {
        error: args.useAppliedFilter
          ? 'No filter is applied on this page to save. Apply a filter on the list first, then try again.'
          : 'No filter to save. Build one with build_filter, or apply a filter on the list page first, then try again.',
      }
    }

    // Runaway-loop guard — this is a PISA write with no confirmation card of its
    // own in the applied-filter path, so nothing else bounds a model re-call loop.
    // Same guard the other write tools use; 'updates' because it edits an existing List.
    const rateCheck = checkRateLimit('updates')
    if (!rateCheck.allowed) {
      return { kind: 'text' as const, content: rateCheck.message ?? '' }
    }

    // Merge into the existing query, preserving sibling keys (order, etc.) —
    // identical to the manual SegmentFilterModal write.
    try {
      await data.update(item, {
        query: { ...(query ?? {}), filters: structure },
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      return { error: `Failed to save filter to segment: ${message}` }
    }
    // Register the successful write in the sliding window — only after success.
    recordCall('updates')

    // Drop the cached filter now the refine flow is done, so a later unrelated build
    // can never read this stale structure.
    if (cacheKey) {
      clearCachedFilter(cacheKey)
    }

    // Recompute membership so the segment's contact list reflects the new filter
    // without a page reload — mirrors the manual modal (DynamicListContactTab).
    data.invalidate(DataClassName.LIST)

    // Take the user to the segment so the refined membership is visible immediately,
    // and return the url so the bot can offer a clickable link — parity with
    // create_segment (a save with no redirect/link left the user with nothing to open).
    const url = await nav.urlFor(item)
    if (url) {
      nav.navigate({ url })
    }

    const title = item.get('title')
    return {
      saved: true,
      segmentId,
      ...(typeof title === 'string' && title.length > 0 ? { title } : {}),
      ...(url ? { url } : {}),
    }
  },
})
